import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { Alert, Button, Card, Input, Select, Space, Typography } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";

import request from "@/api";
import { exportSpecialZoneReservationOrderList } from "@/api/services/specialZoneReservationOrderService";
import { useUserStore } from "@/stores/react/authStore";
import { TableSearchIcon } from "@/views/components/TableSearchIcon";
import { DateRangePicker as ZhDateRangePicker, Table as ZhTable, type TableColumnProps } from "zhihao-ui-react";
import { displayZoneContractCustomerText } from "./zoneContract/zoneContractCustomerLabel";
import {
    aggregateReservationGoodsMetricsBySpec,
    ZONE_RESERVATION_MATERIAL_SPECS
} from "./zoneReservationOrder/zoneReservationDisplay";
import { formatMoney } from "@/utils/money";
import { ZONE_CONTRACT_RESERVATION_ORDER_STATE, ZONE_CONTRACT_SPECIAL_ZONE } from "@zhihaoscm/meta";

export interface ZoneReservationOrderRow {
    specialZoneReservationOrder?: {
        id?: string | number;
        specialZoneId?: number;
        shipName?: string;
        state?: number;
        createdTime?: string;
        transportOrderShipId?: string;
        requirementPlatId?: string;
    };
    specialZoneContract?: { contractCode?: string; specialZoneName?: string; specialZoneId?: number; customerId?: number };
    businessCustomer?: {
        id?: number;
        code?: string;
        applyState?: number;
        institutionName?: string;
        realName?: string;
        nickName?: string;
        mobile?: string;
    };
    buyerUserText?: string;
    ship?: { cnname?: string; name?: string };
    specialZoneReservationOrderGoods?: Array<{
        goodsCode?: number | string;
        plannedTonnage?: number;
        settlementTon?: number;
        settlementAmount?: number;
        unitPrice?: number;
    }>;
}

export interface ZoneReservationOrderResult {
    list?: ZoneReservationOrderRow[];
    total?: number;
}

export interface ZoneReservationOrderQuery {
    page: number;
    size: number;
    flag: 0 | 1;
    contractReservationKeyword?: string;
    customerKeyword?: string;
    shipOrderKeyword?: string;
    states?: number[];
    createStartDate?: string;
    createEndDate?: string;
}

export interface ZoneReservationOrderPageProps {
    exportOrders?: (query: ZoneReservationOrderExportQuery) => Promise<unknown>;
    loadOrders?: (query: ZoneReservationOrderQuery) => Promise<ZoneReservationOrderResult>;
    permissions?: string[];
}

export type ZoneReservationOrderExportQuery = Omit<ZoneReservationOrderQuery, "page" | "size">;

const defaultQuery: ZoneReservationOrderQuery = { page: 1, size: 10, flag: 1 };

/** 复用 Vue 专区预约单分页接口。 */
export async function loadZoneReservationOrders(query: ZoneReservationOrderQuery): Promise<ZoneReservationOrderResult> {
    const response = await request.service.get<ZoneReservationOrderResult>(
        "/admin/special-zone-reservation-order/paging",
        { params: query }
    );
    return response.data;
}

/** 复用 Vue 专区预约单导出接口，导出只提交当前已生效的筛选条件。 */
export async function exportZoneReservationOrders(query: ZoneReservationOrderExportQuery): Promise<unknown> {
    return exportSpecialZoneReservationOrderList(query);
}

/** 从 Vue 分页行中读取专区名称和船名的兼容字段。 */
function getZoneName(row: ZoneReservationOrderRow): string {
    const id = row.specialZoneReservationOrder?.specialZoneId ?? row.specialZoneContract?.specialZoneId;
    return (
        row.specialZoneContract?.specialZoneName ||
        ZONE_CONTRACT_SPECIAL_ZONE.list.find(item => Number(item.code) === Number(id))?.intro ||
        "--"
    );
}

/** 采购用户展示沿用旧 Vue 的企业名称、手机号和用户编码拼接规则。 */
function getBuyerName(row: ZoneReservationOrderRow): string {
    if (row.buyerUserText) return row.buyerUserText;
    const customer = row.businessCustomer;
    return displayZoneContractCustomerText(customer);
}

function getShipName(row: ZoneReservationOrderRow): string {
    return row.specialZoneReservationOrder?.shipName || row.ship?.cnname || row.ship?.name || "--";
}

function reservationStateLabel(state?: number): string {
    return Number(state) === 1 ? "有效" : "已取消";
}

/** 物料子列沿用 Vue 列表的数量/金额显示精度，并保留 0 的有效值。 */
function formatReservationMetric(value: number, field: "quantity" | "money"): string {
    return field === "money"
        ? formatMoney(value)
        : value.toLocaleString("zh-CN", { minimumFractionDigits: 0, maximumFractionDigits: 6 });
}

/** 专区预约单列表，保留 Vue 状态 Tab、筛选字段和详情入口。 */
export function ZoneReservationOrderPage({
    exportOrders = exportZoneReservationOrders,
    loadOrders = loadZoneReservationOrders,
    permissions
}: ZoneReservationOrderPageProps) {
    const navigate = useNavigate();
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    // 路由页面从登录态读取权限，显式传入 permissions 的测试/嵌入调用仍优先。
    const permissionList = permissions ?? storePermissions;
    const canManage = permissionList.length === 0 || permissionList.includes("ROLE_ZONE_PREORDER_MANAGE");
    const canRead =
        permissionList.length === 0 ||
        permissionList.includes("ROLE_ZONE_PREORDER_R") ||
        permissionList.includes("ROLE_ZONE_PREORDER_MANAGE");
    const [query, setQuery] = useState<ZoneReservationOrderQuery>(defaultQuery);
    const [rows, setRows] = useState<ZoneReservationOrderRow[]>([]);
    const [total, setTotal] = useState(0);
    /** 按状态缓存顶部统计卡片，切换页签时保留已加载的另一侧数量。 */
    const [stats, setStats] = useState({ effective: 0, cancelled: 0 });
    const [orderKeyword, setOrderKeyword] = useState("");
    const [userKeyword, setUserKeyword] = useState("");
    const [shipKeyword, setShipKeyword] = useState("");
    const [statusList, setStatusList] = useState<string[]>([]);
    const [createdDateRange, setCreatedDateRange] = useState<[string | null, string | null]>([null, null]);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [exporting, setExporting] = useState(false);

    useEffect(() => {
        let disposed = false;
        void loadOrders(query)
            .then(result => {
                if (disposed) return;
                setRows(result.list || []);
                setTotal(result.total || 0);
                setStats(current => ({
                    ...current,
                    [query.flag === 1 ? "effective" : "cancelled"]: result.total || 0
                }));
                setErrorMessage(null);
            })
            .catch(() => {
                if (!disposed) setErrorMessage("专区预约单加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadOrders, query]);

    /** 旧 Vue 首屏会并行查询有效和已取消两侧总数，保证第二张统计卡不会初始显示 0。 */
    useEffect(() => {
        let disposed = false;
        void loadOrders({ ...defaultQuery, flag: 0 })
            .then(result => {
                if (!disposed) setStats(current => ({ ...current, cancelled: Number(result.total) || 0 }));
            })
            .catch(() => undefined);
        return () => {
            disposed = true;
        };
    }, [loadOrders]);

    const search = () => {
        setQuery({
            ...defaultQuery,
            flag: query.flag,
            ...(orderKeyword.trim() ? { contractReservationKeyword: orderKeyword.trim() } : {}),
            ...(userKeyword.trim() ? { customerKeyword: userKeyword.trim() } : {}),
            ...(shipKeyword.trim() ? { shipOrderKeyword: shipKeyword.trim() } : {}),
            ...(statusList.length ? { states: statusList.map(Number) } : {}),
            ...(createdDateRange[0] ? { createStartDate: createdDateRange[0] } : {}),
            ...(createdDateRange[1] ? { createEndDate: createdDateRange[1] } : {})
        });
    };

    const changeTab = (flag: 0 | 1) => setQuery({ ...defaultQuery, flag });
    /** 沿用 Vue 导出参数，排除分页字段，避免把当前页误当成导出范围。 */
    const exportList = async () => {
        const { page: _page, size: _size, ...exportQuery } = query;
        void _page;
        void _size;
        setExporting(true);
        try {
            await exportOrders(exportQuery);
            setErrorMessage(null);
        } catch {
            setErrorMessage("专区预约单导出失败");
        } finally {
            setExporting(false);
        }
    };
    // 将旧 Vue 表格字段映射为 zhihao-ui-react 的列配置，保持行数据和业务文案不变。
    const reservationColumns = useMemo<TableColumnProps<ZoneReservationOrderRow>[]>(
        () => [
            {
                label: "预约单编号",
                minWidth: 204,
                prop: "id",
                render: ({ row }) => {
                    const id = row.specialZoneReservationOrder?.id;
                    return id && canRead ? (
                        <Button
                            type="link"
                            onClick={() => navigate(`/tradeManage/zoneReservationOrderDetail?id=${id}`)}
                        >
                            {id}
                        </Button>
                    ) : (
                        id || "--"
                    );
                }
            },
            {
                label: "关联合同",
                minWidth: 204,
                prop: "contract",
                render: ({ row }) => row.specialZoneContract?.contractCode || "--"
            },
            { label: "专区名称", minWidth: 176, prop: "zone", render: ({ row }) => getZoneName(row) },
            { label: "采购用户", minWidth: 350, prop: "buyer", render: ({ row }) => getBuyerName(row) },
            { label: "船名", minWidth: 176, prop: "ship", render: ({ row }) => getShipName(row) },
            {
                label: "船运单编号",
                minWidth: 204,
                prop: "transport",
                render: ({ row }) =>
                    row.specialZoneReservationOrder?.transportOrderShipId ||
                    row.specialZoneReservationOrder?.requirementPlatId ||
                    "--"
            },
            ...ZONE_RESERVATION_MATERIAL_SPECS.map(spec => ({
                label: spec.name,
                prop: `material-${spec.id}`,
                children: [
                    {
                        label: "计划量 (吨)",
                        prop: `material-${spec.id}-planned`,
                        minWidth: 130,
                        align: "right" as const,
                        render: ({ row }: { row: ZoneReservationOrderRow }) => {
                            const metrics = aggregateReservationGoodsMetricsBySpec(
                                row.specialZoneReservationOrderGoods as never,
                                ZONE_RESERVATION_MATERIAL_SPECS
                            )[spec.id];
                            return formatReservationMetric(metrics?.plannedTonnage || 0, "quantity");
                        }
                    },
                    {
                        label: "结算吨数 (吨)",
                        prop: `material-${spec.id}-settlement-ton`,
                        minWidth: 130,
                        align: "right" as const,
                        render: ({ row }: { row: ZoneReservationOrderRow }) => {
                            const metrics = aggregateReservationGoodsMetricsBySpec(
                                row.specialZoneReservationOrderGoods as never,
                                ZONE_RESERVATION_MATERIAL_SPECS
                            )[spec.id];
                            return formatReservationMetric(metrics?.settlementTon || 0, "quantity");
                        }
                    },
                    {
                        label: "单价 (元)",
                        prop: `material-${spec.id}-unit-price`,
                        minWidth: 130,
                        align: "right" as const,
                        render: ({ row }: { row: ZoneReservationOrderRow }) => {
                            const metrics = aggregateReservationGoodsMetricsBySpec(
                                row.specialZoneReservationOrderGoods as never,
                                ZONE_RESERVATION_MATERIAL_SPECS
                            )[spec.id];
                            return formatReservationMetric(metrics?.unitPrice || 0, "quantity");
                        }
                    },
                    {
                        label: "结算金额 (元)",
                        prop: `material-${spec.id}-settlement-amount`,
                        minWidth: 130,
                        align: "right" as const,
                        render: ({ row }: { row: ZoneReservationOrderRow }) => {
                            const metrics = aggregateReservationGoodsMetricsBySpec(
                                row.specialZoneReservationOrderGoods as never,
                                ZONE_RESERVATION_MATERIAL_SPECS
                            )[spec.id];
                            return formatReservationMetric(metrics?.settlementAmount || 0, "money");
                        }
                    }
                ]
            })),
            {
                label: "创建时间",
                width: 188,
                prop: "created",
                render: ({ row }) => row.specialZoneReservationOrder?.createdTime?.slice(0, 16) || "--"
            },
            {
                label: "状态",
                width: 148,
                prop: "state",
                render: ({ row }) => reservationStateLabel(row.specialZoneReservationOrder?.state)
            }
        ],
        [canRead, navigate]
    );

    return (
        <section aria-label="zone-reservation-order-page" className="admin-trade-list-page">
            <Card>
                <ZhTable
                    columns={reservationColumns}
                    data={rows}
                    emptyText="暂无预约单"
                    pagination={false}
                    rowKey={row => String(row.specialZoneReservationOrder?.id)}
                    // 宽表使用 auto 布局，按内容计算列宽并保留横向滚动。
                    tableLayout="auto"
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <Typography.Title className="mb-4 mt-0" level={4}>
                                专区预约单
                            </Typography.Title>

                            <div aria-label="专区预约单状态页签" className="admin-trade-stat-cards" role="tablist">
                                <button
                                    aria-selected={query.flag === 1}
                                    aria-label="有效预约单"
                                    className={`admin-trade-stat-card is-selectable ${query.flag === 1 ? "is-active" : ""}`}
                                    role="tab"
                                    type="button"
                                    onClick={() => changeTab(1)}
                                >
                                    <span className="admin-trade-stat-card__label">有效预约单</span>
                                    <span className="admin-trade-stat-card__value admin-trade-stat-card__value--zone">
                                        {stats.effective}
                                    </span>
                                </button>
                                <button
                                    aria-selected={query.flag === 0}
                                    aria-label="已取消预约单"
                                    className={`admin-trade-stat-card is-selectable is-cancelled ${query.flag === 0 ? "is-active" : ""}`}
                                    role="tab"
                                    type="button"
                                    onClick={() => changeTab(0)}
                                >
                                    <span className="admin-trade-stat-card__label">已取消预约单</span>
                                    <span className="admin-trade-stat-card__value admin-trade-stat-card__value--zone">
                                        {stats.cancelled}
                                    </span>
                                </button>
                            </div>

                            {errorMessage ? (
                                <Alert className="mb-4" message={errorMessage} role="alert" type="error" />
                            ) : null}

                            <Space className="admin-table-filter-form mb-4" wrap>
                                {canManage ? (
                                    <Button
                                        aria-label="导出专区预约单"
                                        className="zone-reservation-order-export"
                                        loading={exporting}
                                        onClick={() => void exportList()}
                                    >
                                        导出
                                    </Button>
                                ) : null}
                                <Input
                                    aria-label="预约单或合同关键词"
                                    className="zone-reservation-order-keyword"
                                    placeholder="请输入预约单编号/合同编号搜索"
                                    value={orderKeyword}
                                    onChange={event => setOrderKeyword(event.target.value)}
                                />
                                <Input
                                    aria-label="采购用户关键词"
                                    className="zone-reservation-order-customer"
                                    placeholder="请输入用户ID/个人或机构实名/手机号搜索"
                                    value={userKeyword}
                                    onChange={event => setUserKeyword(event.target.value)}
                                />
                                <Input
                                    aria-label="船名或船运单关键词"
                                    className="zone-reservation-order-ship"
                                    placeholder="请输入船名/船运单编号搜索"
                                    value={shipKeyword}
                                    onChange={event => setShipKeyword(event.target.value)}
                                />
                                <Select
                                    aria-label="预约单状态"
                                    className="zone-reservation-order-state"
                                    mode="multiple"
                                    options={ZONE_CONTRACT_RESERVATION_ORDER_STATE.list.map(item => ({
                                        label: item.intro,
                                        value: String(item.code)
                                    }))}
                                    placeholder="请选择状态"
                                    value={statusList}
                                    onChange={setStatusList}
                                />
                                <ZhDateRangePicker
                                    aria-label="创建日期"
                                    className="zone-reservation-order-date"
                                    endPlaceholder="创建结束日期"
                                    format="YYYY-MM-DD"
                                    rangeSeparator="-"
                                    startPlaceholder="创建开始日期"
                                    type="daterange"
                                    value={createdDateRange}
                                    valueFormat="YYYY-MM-DD"
                                    onChange={(_value, formattedValue) =>
                                        setCreatedDateRange(
                                            formattedValue
                                                ? [formattedValue[0] || null, formattedValue[1] || null]
                                                : [null, null]
                                        )
                                    }
                                />
                                <Button
                                    aria-label="查询预约单"
                                    className="admin-table-search-action zone-reservation-order-search"
                                    icon={<TableSearchIcon name="search" />}
                                    type="primary"
                                    onClick={search}
                                >
                                    搜索
                                </Button>
                            </Space>
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                className="mt-4 self-end"
                                current={query.page}
                                pageSize={query.size}
                                pageSizeOptions={[10, 20, 30, 40, 50, 100]}
                                showSizeChanger
                                total={total}
                                onChange={(page, size) =>
                                    // 对齐旧 Vue ProTable：切换每页条数后回到第一页，普通翻页保留当前页大小。
                                    setQuery(current => ({ ...current, page: size !== current.size ? 1 : page, size }))
                                }
                            />
                        </div>
                    }
                />
            </Card>
        </section>
    );
}

export default ZoneReservationOrderPage;
